import { connect } from 'react-redux'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'
import { getImageUrl } from 'lib/image/imageUtils'
import config from 'constants/config'
import { resolveLocalizedConfigValue } from 'constants/config/resolveLocalizedConfigValue'
import { removeRegionCode } from 'lib/string/regionUtils'
import { getVisibleLanguageOptions, getLanguageAlternateLinks } from 'i18n/util/locale'
import { useIntl } from 'react-intl'
import { LabRemoteConfigKeys } from 'constants/lab'
import UrlLocation from 'src/lib/url/UrlLocation'

const SOCIAL_IMAGE_WIDTH = 1200
const SOCIAL_IMAGE_HEIGHT = 630

function getSocialImageUrl(imageId: string) {
  return getImageUrl(imageId, {
    width: SOCIAL_IMAGE_WIDTH,
    height: SOCIAL_IMAGE_HEIGHT,
    aspectRatio: '1200:630',
    gravity: 'center',
    format: 'jpeg',
    quality: 'eco',
  })
}

interface OpenGraphMetaData {
  description?: string
  image?: string
  imageAlt?: string
  // siteName?: string
  title?: string
  // url?: string
}

type TwitterCardType = 'summary' | 'summary_large_image'/* | 'app' | 'player' */
type TwitterUsername = '@Lux_Escapes' | undefined

interface TwitterMetaData {
  card?: TwitterCardType
  description?: string
  image?: string
  imageAlt?: string
  title?: string
  username?: TwitterUsername
}

interface Props {
  canonical?: string
  customerPortalURL: string
  description?: string
  imageId?: string
  imageUrl?: string
  imageAlt?: string
  noIndex?: boolean
  originalCanonicalURL: string
  regionCode: string
  title?: string
  twitter?: TwitterMetaData
  defer?: boolean
  languagesRemoteConfig?: App.LabRemoteConfigResult
}

export function HeadData(props: Props) {
  const location = useLocation()
  const {
    description,
    twitter,
    imageAlt,
    imageId = config.head.brandOpenGraphImageId,
    imageUrl,
    title,
    customerPortalURL,
    originalCanonicalURL,
    noIndex,
    canonical,
    regionCode,
    defer,
    languagesRemoteConfig,
  } = props

  const intl = useIntl()
  const metaTitle = title || resolveLocalizedConfigValue(intl, config.head.defaultTitle)
  const metaDescription = description || resolveLocalizedConfigValue(intl, config.head.defaultDescription)
  const metaImage = imageUrl || (imageId && getSocialImageUrl(imageId))
  const hasGeneratedImage = !!metaImage && !imageUrl
  const metaImageAlt = imageAlt || metaDescription

  const sourcePath = IS_SSR ? originalCanonicalURL : location.pathname
  const sourceLocation = UrlLocation.fromUrl(sourcePath)
  const languageOptions = getVisibleLanguageOptions(languagesRemoteConfig)

  const redirect = (!sourceLocation?.locale ? null :
      sourceLocation.getRedirectToLocale({
        targetLocale: sourceLocation.locale,
        languageOptions,
        allowCanonical: true,
      })
  ) ?? undefined
  const canonicalLocalePath = UrlLocation.fromUrl(sourcePath, redirect).path
  const path = removeRegionCode(canonicalLocalePath, regionCode)
  const alternatePath = canonical ?? path
  const altFirstSegment = alternatePath.split('/')[1]
  const bareAlternatePath = altFirstSegment && languageOptions.some(opt => opt.slug === altFirstSegment || opt.locale === altFirstSegment)
    ? ['', ...alternatePath.split('/').slice(2)].join('/') || '/'
    : alternatePath
  let languageAlternateLinks = getLanguageAlternateLinks({
    customerPortalURL,
    path: bareAlternatePath,
    languageOptions,
  })
  languageAlternateLinks = [] // CX TODO: Temporarily set it to empty to avoid crawlers costing LLM

  const ogMeta: OpenGraphMetaData = {
    title: metaTitle,
    image: metaImage,
    description: metaDescription,
    imageAlt: metaImageAlt,
  }

  const twitterMeta: TwitterMetaData = {
    card: 'summary_large_image' as TwitterCardType,
    username: config.BRAND === 'luxuryescapes' ? '@Lux_Escapes' : undefined as TwitterUsername,
    title: twitter?.title || metaTitle,
    image: twitter?.image ? getSocialImageUrl(twitter.image) : metaImage,
    description: twitter?.description || metaDescription,
    imageAlt: twitter?.imageAlt || metaImageAlt,
  }

  // Small trick to avoid Helmet adding alternate tags that are not canonical in accommodation and bedbank offer pages.
  const pauseAddingAlternateTags = !canonical && (path.includes('/offer/') || path.includes('/partner/'))

  return (
    <Helmet defer={defer}>
      <title>{metaTitle}</title>
      <meta name="title" content={metaTitle}></meta>
      <meta name="description" content={metaDescription}></meta>
      <link
        rel="canonical"
        href={`${customerPortalURL}/${regionCode.toLowerCase()}${canonical ?? (path)}`}
      />
      {noIndex &&
        <meta name="robots" content="noindex, follow" />}
      <meta name="robots" content="max-image-preview:large" />
      {!pauseAddingAlternateTags && languageAlternateLinks.map(link => (
        <link
          key={link.hrefLang}
          rel="alternate"
          href={link.href}
          hrefLang={link.hrefLang}
        />
      ))}
      <link rel="alternate" href={`${customerPortalURL}/au${bareAlternatePath}`} hrefLang="x-default" />

      <meta property="og:type" content="website"></meta>
      <meta property="og:title" content={ogMeta.title}></meta>
      <meta property="og:description" content={ogMeta.description}></meta>
      {ogMeta.image && <meta property="og:image" content={ogMeta.image} itemProp="image"></meta>}
      {ogMeta.image && <meta property="og:image:alt" content={ogMeta.imageAlt}></meta>}
      {hasGeneratedImage && <meta property="og:image:width" content={SOCIAL_IMAGE_WIDTH.toString()}></meta>}
      {hasGeneratedImage && <meta property="og:image:height" content={SOCIAL_IMAGE_HEIGHT.toString()}></meta>}
      {hasGeneratedImage && <meta property="og:image:type" content="image/jpeg"></meta>}
      <meta property="og:url" content={`${customerPortalURL}/${regionCode.toLowerCase()}${path}`}></meta>
      <meta property="og:site_name" content={config.title}></meta>

      <meta name="twitter:title" content={twitterMeta.title}></meta>
      <meta name="twitter:description" content={twitterMeta.description}></meta>
      <meta name="twitter:card" content={twitterMeta.card}></meta>
      {twitterMeta.image && <meta name="twitter:image" content={twitterMeta.image}></meta>}
      {twitterMeta.image && <meta name="twitter:image:alt" content={twitterMeta.imageAlt}></meta>}
      {twitterMeta.username && <meta name="twitter:site" content={twitterMeta.username}></meta>}
    </Helmet>
  )
}

function mapStateToProps(state: App.State) {
  return {
    regionCode: state.geo.currentRegionCode,
    customerPortalURL: state.system.customerPortalURL,
    originalCanonicalURL: state.system.originalCanonicalURL,
    languagesRemoteConfig: state.system.features.remoteConfigFlags[LabRemoteConfigKeys.languages],
  }
}

export default connect(mapStateToProps)(HeadData)
